import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, Plus } from 'lucide-react'
import { Badge } from '~/components/ui/badge'
import type { BadgeTone } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { cn } from '~/lib/utils'
import { IndicatorDetail } from '~/features/monitoring/indicator-detail'
import { getDeviations } from '~/api/monitoring'
import type { DeviationDetail } from '~/api/types'

const TONE: Record<string, BadgeTone> = {
    이탈: 'danger',
    근접: 'warning',
    적합: 'success',
}

// 선택 지표 배너 색(판정별).
const BANNER: Record<string, string> = {
    이탈: 'bg-point-subtle text-point',
    근접: 'bg-warning-subtle text-warning',
    적합: 'bg-success-subtle text-success',
}

/** 기준 이탈 점검 — 이탈·근접 지표의 추이·위험 평가항목·기준 해설. */
export function DeviationsSection({ dashboardId }: { dashboardId: string }) {
    const navigate = useNavigate()
    const [items, setItems] = useState<DeviationDetail[] | null>(null)
    const [sel, setSel] = useState(0)

    useEffect(() => {
        // 전 정량지표를 불러와(이탈>근접>적합 순) 어떤 지표든 시계열 분석 가능.
        getDeviations(dashboardId, true)
            .then((d) => {
                setItems(d)
                setSel(0)
            })
            .catch(() => setItems([]))
    }, [dashboardId])

    if (items === null) {
        return <p className='text-muted-foreground text-sm'>불러오는 중…</p>
    }
    if (items.length === 0) {
        return (
            <div className='border-border bg-card flex items-center gap-3 rounded-md border p-5'>
                <CheckCircle2 className='text-success size-6' />
                <div className='min-w-0 flex-1'>
                    <p className='text-foreground text-sm font-semibold'>
                        정량지표가 없어요
                    </p>
                    <p className='text-muted-foreground text-xs'>
                        연결된 평가의 체크리스트에 정량지표가 없습니다.
                    </p>
                </div>
                <Button
                    size='sm'
                    onClick={() =>
                        navigate(`/monitoring/${dashboardId}/settings`)
                    }>
                    <Plus />
                    데이터 적재
                </Button>
            </div>
        )
    }

    const issues = items.filter(
        (d) => d.verdict === '이탈' || d.verdict === '근접'
    ).length

    const active = items[Math.min(sel, items.length - 1)]

    return (
        <div className='grid grid-cols-[240px_minmax(0,1fr)] gap-4 max-md:grid-cols-1'>
            <aside className='border-border bg-card h-max rounded-md border p-2'>
                <p className='text-muted-foreground px-2 py-1 text-xs'>
                    정량지표 {items.length}개 · 이탈·근접 {issues}개
                </p>
                <ul className='grid gap-1'>
                    {items.map((d, i) => (
                        <li key={d.fieldId}>
                            <button
                                type='button'
                                onClick={() => setSel(i)}
                                className={cn(
                                    'flex w-full items-center gap-2 overflow-hidden rounded-md py-2 pr-2 pl-3 text-left text-sm transition-colors',
                                    i === sel
                                        ? 'bg-accent text-foreground font-semibold'
                                        : 'text-foreground hover:bg-muted/60'
                                )}>
                                <span className='min-w-0 flex-1 truncate'>
                                    {d.nameKo}
                                </span>
                                <Badge
                                    tone={TONE[d.verdict] ?? 'neutral'}
                                    className='shrink-0 px-1.5 text-[11px] whitespace-nowrap'>
                                    {d.verdict}
                                </Badge>
                            </button>
                        </li>
                    ))}
                </ul>
            </aside>

            <div className='grid min-w-0 gap-4'>
                <div
                    className={cn(
                        'min-w-0 rounded-md px-4 py-3 text-sm break-words',
                        BANNER[active.verdict] ??
                            'bg-muted text-muted-foreground'
                    )}>
                    <b>
                        {active.nameKo}
                        {active.unit ? ` (${active.unit})` : ''}
                    </b>{' '}
                    — {active.basis}
                </div>

                <IndicatorDetail detail={active} />
            </div>
        </div>
    )
}
